import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form, Input, Row, Col } from 'antd';
import { QUESTION_TYPES, SUB_TYPES } from 'shared/constants/survey';
import useFormatMessage from 'hooks/useFormatMessage';

import ActiveInput from 'components/ActiveInput';
import findSurveyContent from 'shared/utils/findSurveyContent';
import TranslationTabs from 'components/TranslationTabs';
import { BENCHMARK_CATEGORIES } from 'shared/constants/benchmark';
import EditOptions from './EditOptions';

import messages from './messages';
import EditQuestionSettings, { questionTabs, questionTypes } from './EditQuestionSettings';
import EditDependencies from './EditDependencies';

function getCategoryIds(categories, titles) {
  return categories?.filter(cat => titles.includes(cat.title)).map(cat => cat.id);
}

function getCategoryId(categories, titles) {
  const cats = getCategoryIds(categories, titles);

  return cats?.length ? cats[0] : null;
}

function getInitialValues(surveyContent, dependencies, survey, translations) {
  const { active, dependencyOperator, question } = surveyContent;
  const { title, description, categories, options, settings, group } = question;
  const { type, subType, required, random, lockLastOption, rows, min, max, email } = settings;

  const translatedContent = translations.reduce((acc, t) => {
    const content = findSurveyContent(t.root, c => c.question?.group?.id === group?.id);
    if (content) {
      acc.push({
        ...content,
        surveyId: t.id,
      });
    }
    return acc;
  }, []);

  const initialValues = {
    active,
    title,
    description,
    tab: getCategoryId(categories, questionTabs),
    types: getCategoryIds(categories, questionTypes),
    benchmark: getCategoryId(categories, BENCHMARK_CATEGORIES),
    options: options?.map(o => ({
      ...o,
      translations: o.translations || translatedContent.reduce((acc, c) => {
        const translationOption = o.group && c.question?.options?.find(to => to.group?.id === o.group.id);

        if (translationOption) {
          acc[c.surveyId] = translationOption;
        }

        return acc;
      }, {}),
    })),
    dependencies,
    dependencyOperator: dependencyOperator || 'AND',
    type,
    subType: subType || SUB_TYPES.TEXTAREA,
    required,
    email,
    rows,
    random,
    lockLastOption,
    min,
    max,
    translations: question.translations || translatedContent.reduce((acc, c) => {
      acc[c.surveyId] = {
        ...c.question,
        active: c.active,
      };

      return acc;
    }, {}),
  };

  return initialValues;
}

const PrimaryContent = ({ survey, surveyContent, surveyQuestions }) => {
  const $t = useFormatMessage();
  const type = Form.useWatch('type');
  const showOptions = [QUESTION_TYPES.CHECKBOX, QUESTION_TYPES.RADIO, QUESTION_TYPES.GRADE].includes(type);

  return (
    <>
      <Row gutter={8} align="bottom">
        <Col flex="auto">
          <Form.Item
            name="title"
            label={<FormattedMessage {...messages.questionTitle} />}
            rules={[{ required: true, message: $t(messages.questionTitleRequired) }]}
          >
            <Input placeholder={$t(messages.questionTitlePlaceholder)} />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name="active"
          >
            <ActiveInput />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="description" label={<FormattedMessage {...messages.questionDescription} />}>
        <Input />
      </Form.Item>

      {showOptions && (
      <Form.Item label={<FormattedMessage {...messages.options} />}>
        <EditOptions name="options" showValue={[QUESTION_TYPES.GRADE, QUESTION_TYPES.NPS].includes(type)} />
      </Form.Item>
      )}

      <EditQuestionSettings survey={survey} type={type} />
      <EditDependencies questions={surveyQuestions.filter(q => q.id !== surveyContent.question.id)} />
    </>
  );
};

const TranslationContent = ({ translation }) => {
  const $t = useFormatMessage();
  const type = Form.useWatch('type');
  const showOptions = [QUESTION_TYPES.CHECKBOX, QUESTION_TYPES.RADIO, QUESTION_TYPES.GRADE].includes(type);

  return (
    <>
      <Form.Item noStyle name={['translations', translation.id, 'id']}>
        <input type="hidden" />
      </Form.Item>
      <Row gutter={8} align="bottom">
        <Col flex="auto">
          <Form.Item
            name={['translations', translation.id, 'title']}
            label={<FormattedMessage {...messages.questionTitle} />}
          >
            <Input placeholder={$t(messages.questionTitlePlaceholder)} />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={['translations', translation.id, 'active']}
          >
            <ActiveInput />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name={['translations', translation.id, 'description']} label={<FormattedMessage {...messages.questionDescription} />}>
        <Input />
      </Form.Item>

      {showOptions && (
      <Form.Item label={<FormattedMessage {...messages.options} />}>
        <EditOptions name="options" fieldName={['translations', translation.id]} isTranslation />
      </Form.Item>
      )}

    </>
  );
};

const EditQuestion = ({
  survey,
  surveyQuestions,
  translations,
  surveyContent,
  dependencies,
  form,
  onSubmit,
}) => {
  const initialValues = useMemo(() => getInitialValues(surveyContent, dependencies, survey, translations), [surveyContent, dependencies, survey, translations]);

  useEffect(() => {
    if (surveyContent) {
      form.resetFields();
    }
  }, [surveyContent]);

  return (
    <Form layout="vertical" form={form} onFinish={onSubmit} initialValues={initialValues}>
      <TranslationTabs
        primary={survey}
        translations={translations}
        primaryContent={<PrimaryContent survey={survey} surveyContent={surveyContent} surveyQuestions={surveyQuestions} />}
        translationsContent={translations.map(translation => <TranslationContent translation={translation} />)}
      />
    </Form>
  );
};

export default EditQuestion;
